import React from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCube, faEdit} from "@fortawesome/free-solid-svg-icons";
import btLockIcon from "../../assets/btlock.png";
import eLockIcon from "../../assets/gps_lock.png";

// ==================== CONSTANTS ====================
export const COLORS = ["#4318FF", "#05CD99", "#6AD2FF", "#FFB547", "#FF6B6B"];

export const dashboardStyles = {
  gridContainer: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 2fr",
    gap: "20px",
    marginBottom: "20px",
    height: "180px",
  },
  compactCard: {
    background: "white",
    borderRadius: "12px",
    padding: "0 20px",
    boxShadow: "0px 3px 10px rgba(0,0,0,0.03)",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    height: "100%",
  },
  iconBox: {
    width: "50px",
    height: "50px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    fontSize: "20px",
    marginRight: "15px",
    flexShrink: 0,
  },
  cardContent: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  cardTitle: {
    color: "#a3aed0",
    fontSize: "13px",
    fontWeight: "600",
    marginBottom: "5px",
  },
  cardValue: {
    color: "#2b3674",
    fontSize: "24px",
    fontWeight: "700",
    lineHeight: "1",
  },
  chartArea: {
    position: "relative",
    height: "100%",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  donutCenter: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
    zIndex: 10,
  },
  centerValue: {
    fontSize: "32px",
    fontWeight: "800",
    color: "#2b3674",
    lineHeight: "1.2",
    marginBottom: "-5px",
  },
  centerLabel: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#a3aed0",
  },
  centerIcon: {
    fontSize: "18px",
    color: "#4318FF",
    marginBottom: "5px",
    background: "rgba(67, 24, 255, 0.1)",
    padding: "8px",
    borderRadius: "50%",
    width: "34px",
    height: "34px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
};

// ==================== TABLE STYLES ====================
export const customStyles = {
  headRow: {
    style: {
      backgroundColor: "#f9fafb",
      borderBottom: "2px solid #e9ecf2",
      minHeight: "40px",
    },
  },
  headCells: {
    style: {
      color: "#6b7280",
      fontSize: "11px",
      fontWeight: "600",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      paddingLeft: "16px",
      paddingRight: "16px",
    },
  },
  rows: {
    style: {
      minHeight: "45px",
      fontSize: "13px",
      fontWeight: "500",
      color: "#1f2937",
      borderBottom: "1px solid #f3f4f6",
      transition: "all 0.2s",
      cursor: "pointer",
      "&:hover": {
        backgroundColor: "#f9fafb",
      },
    },
  },
  cells: {
    style: {
      paddingLeft: "16px",
      paddingRight: "16px",
    },
  },
};

// ==================== TABLE COLUMN CONFIGS ====================
export const getClientColumns = (onToggleClientStatus, onOpenEditModal) => [
  {
    name: "Company",
    selector: (row) => row.name,
    sortable: true,
    grow: 2,
    cell: (row) => (
      <div className="company-cell" style={{ pointerEvents: "none" }}>
        <div
          className="company-avatar"
          style={{ width: "32px", height: "32px", fontSize: "14px" }}
        >
          {row.name.charAt(0)}
        </div>
        <div className="company-info">
          <div className="company-name" style={{ fontSize: "13px" }}>
            {row.name}
          </div>
          <div className="company-subtitle" style={{ fontSize: "11px" }}>
            {row.description || "No description"}
          </div>
        </div>
      </div>
    ),
  },
  {
    name: "Contact Person",
    selector: (row) => row.contactName,
    grow: 1,
    cell: (row) => (
      <div className="table-text" style={{ pointerEvents: "none" }}>
        {row.contactName}
      </div>
    ),
  },
  {
    name: "Email",
    selector: (row) => row.emailId,
    grow: 1.5,
    cell: (row) => (
      <div className="table-email" style={{ pointerEvents: "none" }}>
        {row.emailId}
      </div>
    ),
  },
  {
    name: "Phone",
    selector: (row) => row.phoneNumber,
    grow: 1,
    cell: (row) => (
      <div className="table-text" style={{ pointerEvents: "none" }}>
        {row.phoneNumber || "—"}
      </div>
    ),
  },
  {
    name: "Status",
    width: "150px",
    center: true,
    cell: (row) => (
      <div
        className="client-status-toggle"
        onClick={(e) => onToggleClientStatus(row, e)}
        style={{ pointerEvents: "auto" }}
        title="Click to change client status"
      >
        <div
          className={`toggle-switch ${
            row.status === "ACTIVE" ? "toggle-on" : "toggle-off"
          }`}
        >
          <div className="toggle-knob" />
        </div>
        <span
          className={`toggle-label ${
            row.status === "ACTIVE" ? "label-active" : "label-inactive"
          }`}
        >
          {row.status}
        </span>
      </div>
    ),
  },
  {
    name: "Actions",
    width: "110px",
    center: true,
    cell: (row) => (
      <button
        className="edit-icon-btn"
        onClick={(e) => onOpenEditModal(row, e)}
        title="Edit Client"
        style={{ pointerEvents: "auto" }}
      >
        <FontAwesomeIcon icon={faEdit} />
      </button>
    ),
  },
];

export const getAssetColumns = (onToggleAssetStatus) => [
  {
    name: "Asset ID",
    selector: (row) => row.assetUnitId,
    width: "120px",
    cell: (row) => <span className="asset-id">{row.assetUnitId}</span>,
  },
  {
    name: "Name",
    selector: (row) => row.name,
    grow: 1.5,
    cell: (row) => <span className="asset-name">{row.name}</span>,
  },
  {
    name: "Type",
    selector: (row) => row.assetType,
    sortable: true,
    grow: 1,
  },
  {
    name: "Status",
    width: "150px",
    center: true,
    cell: (row) => (
      <div
        className="asset-status-toggle"
        onClick={(e) => onToggleAssetStatus(row.assetId, row.assetStatus, e)}
        style={{ pointerEvents: "auto" }}
        title="Click to toggle asset status"
      >
        <div
          className={`toggle-switch ${
            row.assetStatus === "ACTIVE" || row.assetStatus === "ASSIGNED"
              ? "toggle-on"
              : "toggle-off"
          }`}
        >
          <div className="toggle-knob" />
        </div>
        <span
          className={`toggle-label ${
            row.assetStatus === "ACTIVE" || row.assetStatus === "ASSIGNED"
              ? "label-active"
              : "label-inactive"
          }`}
        >
          {row.assetStatus}
        </span>
      </div>
    ),
  },
];

export const getUserColumns = () => [
  {
    name: "User",
    selector: (row) => row.fullName,
    grow: 2,
    cell: (row) => (
      <div className="user-cell">
        <div className="user-avatar">{row.fullName?.charAt(0) || "U"}</div>
        <div className="user-info">
          <div className="user-name">{row.fullName}</div>
          <div className="user-username">@{row.username}</div>
        </div>
      </div>
    ),
  },
  {
    name: "Role",
    selector: (row) => row.userRole,
    sortable: true,
    cell: (row) => <span className="role-badge">{row.userRole}</span>,
  },
];

// ==================== UTILITY FUNCTIONS ====================
export const getAssetTypeData = (list) => {
  const counts = {};
  list.forEach((a) => {
    counts[a.assetType || "Unknown"] = (counts[a.assetType] || 0) + 1;
  });
  return Object.keys(counts).map((key) => ({
    name: key,
    value: counts[key],
  }));
};

export const getAssetTypeCounts = (assets) => {
  const counts = {};
  assets.forEach((asset) => {
    const type = asset.assetType || "Unknown";
    counts[type] = (counts[type] || 0) + 1;
  });
  return counts;
};

export const getAssetTypeIcon = (type) => {
  const typeUpper = (type || "").toUpperCase();

  if (typeUpper.includes("BT_LOCK")) {
    return {
      kind: "image",
      value: btLockIcon,
      size: "large",
    };
  }

  if (typeUpper.includes("ELOCK")) {
    return {
      kind: "image",
      value: eLockIcon,
      size: "normal",
    };
  }

  return {
    kind: "fa",
    value: faCube,
    size: "normal",
  };
};
